import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BaseDto } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";
import { notifications_api_requests } from "..";

const PATH = "/notifications";

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Notification Channel Configuration
 * Represents the configuration for a notification channel (SMS, EMAIL, PUSH, WHATSAPP)
 *
 * Note: The `defaultProviderChannelId` field references the junction table (provider-channel association ID)
 * from the `communication_provider_channels` table. This replaced the old `defaultProviderId` field due to
 * the many-to-many relationship between providers and channels. The junction table allows providers to
 * support multiple channels with independent configurations (isEnabled, priority, etc.).
 */
export interface NotificationChannel extends BaseDto {
  channelCode: string;
  channelName: string;
  isEnabled: boolean;
  defaultProviderChannelId: number | null; // References junction table (provider-channel association)
  defaultProviderCode: string | null;
  defaultProviderName: string | null;
  rateLimitPerMinute: number;
  rateLimitPerDay: number;
}

/**
 * Request body for updating channel configuration
 */
export interface NotificationChannelConfig {
  isEnabled: boolean;
  defaultProviderCode?: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const NOTIFICATION_CHANNELS_QUERY_KEY = "notifications-channels";

// ============================================================================
// Channel Configuration Hooks
// ============================================================================

/**
 * Fetch all notification channel configurations
 * GET /api/v1/notifications/channels/configs
 */
export const useGetChannelConfigs = () => {
  return useQuery({
    queryKey: [NOTIFICATION_CHANNELS_QUERY_KEY, "configs"],
    queryFn: async (): Promise<NotificationChannel[]> =>
      notifications_api_requests.get(`${PATH}/channels/configs`),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch a specific notification channel configuration by channel code
 * GET /api/v1/notifications/channels/{channelCode}/config
 * @param channelCode - The channel code (SMS, EMAIL, PUSH, WHATSAPP)
 */
export const useGetChannelConfig = (channelCode?: string) => {
  return useQuery({
    queryKey: [NOTIFICATION_CHANNELS_QUERY_KEY, "config", channelCode],
    queryFn: async (): Promise<NotificationChannel> =>
      notifications_api_requests.get(`${PATH}/channels/${channelCode}/config`),
    enabled: !!channelCode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Update a notification channel configuration
 * PUT /api/v1/notifications/channels/{channelCode}/config
 */
export const useUpdateChannelConfig = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      channelCode,
      config,
    }: {
      channelCode: string;
      config: NotificationChannelConfig;
    }): Promise<NotificationChannel> =>
      notifications_api_requests.put(
        `${PATH}/channels/${channelCode}/config`,
        config,
      ),

    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_CHANNELS_QUERY_KEY],
      });
      setSuccessNotification(
        `Channel ${data.channelName} configuration updated successfully`,
      );
    },

    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        "Failed to update channel configuration";
      setErrorNotification(message);
    },
  });
};

// Made with Bob
